/**
 * SkillCoverageSection — unit and team skill coverage overview for the mentor board.
 *
 * Renders the team's policy-mandated skills alongside current holders and scheduled
 * shift slot coverage. Enables unit supervisors to spot coverage gaps and impending
 * qualification expiries in real time.
 */

import { useTranslation } from 'react-i18next';
import { AlertCircle, ShieldAlert, Users } from 'lucide-react';

import { cn } from '@/lib/utils';
import { ApiError } from '@/api/client';
import { Skeleton } from '@/components/ui/skeleton';
import { useTeamSkills } from '@/hooks/useTeamSkills';
import { demandMinimum, type TeamSkillRow } from '@/api/endpoints/teamSkills';
import { teamSkillTitle } from '@/lib/skillTitle';
import { periodLastDay } from '@/utils/dates';

export interface SkillCoverageSectionProps {
  teamId: string;
  period?: string;
  className?: string;
}

function hasStatus(err: object): err is { status: unknown } {
  return 'status' in err;
}

function isAccessDenied(error: unknown): boolean {
  if (error instanceof ApiError) {
    return error.status === 403;
  }
  if (typeof error === 'object' && error !== null && hasStatus(error)) {
    return error.status === 403;
  }
  return false;
}

export function SkillCoverageSection({
  teamId,
  period,
  className,
}: SkillCoverageSectionProps) {
  const { t } = useTranslation();
  const { data, isLoading, error } = useTeamSkills(teamId, period);

  if (isAccessDenied(error)) {
    return (
      <section
        data-testid="skill-coverage-section"
        className={cn('space-y-3', className)}
      >
        <div
          data-testid="skill-coverage-denied"
          role="alert"
          className="rounded-[18px] border border-amber-200 bg-amber-50/60 p-4 text-[14px] text-amber-900"
        >
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4 shrink-0 text-amber-600" aria-hidden="true" />
            <p className="font-semibold">
              {t('mentor.skillCoverage.notYourTeam', 'you do not manage this team')}
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section
        data-testid="skill-coverage-section"
        className={cn('space-y-3', className)}
      >
        <div
          role="alert"
          className="rounded-[18px] border border-rose-200 bg-rose-50/60 p-4 text-[14px] text-rose-900"
        >
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 shrink-0 text-rose-600" aria-hidden="true" />
            <p className="font-semibold">
              {t('mentor.skillCoverage.loadError', 'Could not load skill coverage')}
            </p>
          </div>
        </div>
      </section>
    );
  }

  if (isLoading) {
    return (
      <section
        data-testid="skill-coverage-section"
        className={cn('space-y-3', className)}
        aria-busy="true"
      >
        <Skeleton className="h-6 w-44" />
        <div className="space-y-2">
          <Skeleton className="h-28 w-full rounded-[18px]" />
          <Skeleton className="h-28 w-full rounded-[18px]" />
        </div>
      </section>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <section
      data-testid="skill-coverage-section"
      className={cn('space-y-3', className)}
    >
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-[15px] font-semibold text-neutral-900">
          <Users className="h-4 w-4 text-neutral-400" aria-hidden="true" />
          {t('mentor.skillCoverage.title', 'Skill Coverage')}
        </h2>
        {data.subject.display && (
          <span className="text-[13px] text-neutral-500">
            {data.subject.display}
          </span>
        )}
      </div>

      {data.items.length === 0 ? (
        <div className="rounded-[18px] border border-neutral-200 bg-white p-6 text-center">
          <p className="text-[14px] text-neutral-500">
            {t('mentor.skillCoverage.empty', 'No skill coverage requirements recorded for this team')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Rows are rendered in the exact order returned by the API. We deliberately do
              NOT sort client-side: the backend orders most-short-first, and a second sort
              here would duplicate that business rule and create discrepancies with server logic. */}
          {data.items.map((row) => (
            // The key carries the SELECTOR, not the skill alone. Two demand
            // rows may name one skill for two roles or two shift types, and the
            // contract says the view reports one line per row and never merges
            // them — so `skill_id` alone is a duplicate key by design rather
            // than by accident, and React reconciles two distinct rows as one.
            <SkillCoverageRowCard
              key={[
                row.required.skill_id,
                row.required.shift_type ?? '',
                row.required.applies_to.role ?? '',
                row.required.applies_to.team_kind ?? '',
                row.required.applies_to.team_ref ?? '',
              ].join('|')}
              row={row}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function SkillCoverageRowCard({ row }: { row: TeamSkillRow }) {
  const { t, i18n } = useTranslation();
  const title = teamSkillTitle(row, i18n.language);
  const demand = demandMinimum(row.required);

  return (
    <div
      data-testid="skill-coverage-row"
      data-skill-id={row.required.skill_id}
      className="rounded-[18px] border border-neutral-200 bg-white p-4"
    >
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="text-[15px] font-semibold text-neutral-900">{title}</h3>
          <p className="mt-0.5 text-[13px] text-neutral-500">
            {demand.kind === 'per_shift'
              ? t('mentor.skillCoverage.minimumPerShift', {
                  count: demand.value,
                  defaultValue: `Required: minimum ${demand.value} per shift`,
                })
              : t('mentor.skillCoverage.minimumHolders', {
                  count: demand.value,
                  defaultValue: `Required: minimum ${demand.value} holders`,
                })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
          <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 font-semibold text-neutral-700">
            {t('mentor.skillCoverage.holderCountBadge', {
              count: row.holder_count,
              defaultValue: `${row.holder_count} holders`,
            })}
          </span>
          <span className="rounded-full border border-neutral-200 bg-neutral-50 px-2.5 py-0.5 font-semibold text-neutral-700">
            {t('mentor.skillCoverage.onShiftCountBadge', {
              count: row.on_shift_now_count,
              defaultValue: `${row.on_shift_now_count} on shift now`,
            })}
          </span>
        </div>
      </div>

      {demand.kind === 'per_shift' && row.shifts && (
        <div className="mt-3 space-y-2">
          <p className="text-[12px] font-semibold uppercase tracking-wide text-neutral-500">
            {t('mentor.skillCoverage.shiftCoverageTitle', 'Shift Coverage')}
          </p>
          {row.shifts.length === 0 ? (
            <p className="text-[13px] text-neutral-400">
              {t('mentor.skillCoverage.noRosteredShifts', 'No shifts scheduled in this window')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {row.shifts.map((shift) => {
                const isShort = shift.gap > 0;
                return (
                  <div
                    key={`${shift.date}:${shift.shift_type}:${shift.period.start}`}
                    data-testid="skill-coverage-slot"
                    data-date={shift.date}
                    data-shift-type={shift.shift_type}
                    data-gap={shift.gap}
                    className={cn(
                      'flex items-center gap-2 rounded-lg border px-2.5 py-1.5 text-[12px]',
                      isShort
                        ? 'border-amber-300 bg-amber-50 font-semibold text-amber-900'
                        : 'border-neutral-200 bg-neutral-50 text-neutral-700',
                    )}
                  >
                    <span className="tabular-nums">{shift.date}</span>
                    <span className="rounded bg-neutral-200/80 px-1 py-0.5 text-[10px] font-bold uppercase text-neutral-800">
                      {shift.shift_type}
                    </span>
                    <span
                      className={cn(
                        'tabular-nums',
                        isShort ? 'text-amber-900' : 'text-neutral-500',
                      )}
                    >
                      {isShort
                        ? t('mentor.skillCoverage.slotShortGap', {
                            gap: shift.gap,
                            defaultValue: `Gap: ${shift.gap} short`,
                          })
                        : t('mentor.skillCoverage.slotMetGap', {
                            gap: shift.gap,
                            defaultValue: 'Gap: 0',
                          })}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      <div className="mt-3 space-y-1.5 border-t border-neutral-100 pt-3">
        <p className="text-[12px] font-semibold uppercase tracking-wide text-neutral-500">
          {t('mentor.skillCoverage.holdersHeader', 'Holders')} ({row.holders.length})
        </p>
        {row.holders.length === 0 ? (
          // Not a repeat of the count badge above. Empty IS the answer here, and
          // it is the one a lead most needs to act on, so the row says what the
          // absence means rather than printing the same zero twice.
          <p data-testid="skill-coverage-no-holders" className="text-[13px] text-neutral-400">
            {t(
              'mentor.skillCoverage.zeroHolders',
              'Nobody on this team holds this skill',
            )}
          </p>
        ) : (
          <ul className="space-y-1.5">
            {row.holders.map((holder) => {
              const displayName = holder.user.display ?? holder.user.reference ?? '';
              // Required key, nullable value: the schema refuses a holder with
              // no `period` at all, so `?.` would hide a parse defect here.
              const lastCoveredDay =
                holder.period === null || holder.period.end === null
                  ? null
                  : periodLastDay(holder.period.end);
              return (
                <li
                  key={holder.user.reference ?? displayName}
                  className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-neutral-50 px-2.5 py-1 text-[13px]"
                >
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-neutral-900">{displayName}</span>
                    <span className="rounded border border-neutral-200 bg-white px-1.5 py-0.5 text-[11px] text-neutral-600">
                      {holder.basis}
                    </span>
                    {holder.on_shift_now && (
                      <span className="rounded border border-emerald-200 bg-emerald-50 px-1.5 py-0.5 text-[11px] font-semibold text-emerald-700">
                        {t('mentor.skillCoverage.onShiftNow', 'On shift')}
                      </span>
                    )}
                  </div>
                  {/*
                    THE DATE, AND NO STATUS DERIVED FROM IT. This used to compute
                    `lapsed` from `Date.now()` and colour the row for it, which
                    is the client inventing an answer the server did not send,
                    and it was wrong twice over. On a read of a PAST window it
                    marked a nurse lapsed who was fully qualified throughout the
                    window being reported on. And the date is inclusive of its
                    own day, while `new Date("...")` parses to that day's first
                    instant, so a certificate expiring today read as already gone
                    from one minute past midnight.

                    Only the server knows the window this row answers for, so
                    only the server can say "lapsed". Until it sends that, the
                    date is the honest answer: a date already past reads as
                    lapsed to the person reading it, which is what it is.

                    `holder.period` is half-open, so its `end` is the first
                    instant NOT covered and the day to show is the one before —
                    the inverse of the conversion the client makes on the way in.
                    A null period is a declaration, which has no interval; a null
                    `end` is a qualification that does not expire. Both are
                    claims, and neither earns a placeholder.
                  */}
                  {lastCoveredDay !== null && (
                    <span className="tabular-nums text-[12px] text-neutral-500">
                      {t('mentor.skillCoverage.validUntilDate', {
                        date: lastCoveredDay,
                        defaultValue: `Valid until ${lastCoveredDay}`,
                      })}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
